import { useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCollection, useUpdateCollection } from "../api/collections";
import { LazyMarkdown } from "../documents/LazyMarkdown";
import type { EditorHandle } from "../editor/types";
import { PageHeader } from "../layout/PageHeader";
import { Button } from "../ui/Button";
import { useToast } from "../ui/toast-context";

/**
 * `/collection/:slug/overview/edit`. The overview is a rich-text body like any
 * other, so it is written with the editor and saved as `data` — the server
 * derives the plain description from it.
 */
export function CollectionOverviewEdit(): ReactNode {
  const { slug } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const query = useCollection(slug);
  const update = useUpdateCollection();
  const handleRef = useRef<EditorHandle | null>(null);
  const [dirty, setDirty] = useState(false);

  const collection = query.data?.collection;

  if (query.isPending) {
    return (
      <p role="status" className="text-sm text-muted">
        Loading collection…
      </p>
    );
  }
  if (query.isError || collection === undefined) {
    return (
      <p role="alert" className="text-sm text-danger">
        {query.error?.message ?? "This collection could not be loaded."}
      </p>
    );
  }

  const back = `/collection/${slug ?? ""}/overview`;

  return (
    <>
      <PageHeader>
        <h1 className="min-w-0 flex-1 truncate text-base font-semibold">
          {collection.name} — overview
        </h1>
        <div className="flex shrink-0 gap-2">
          <Button
            size="sm"
            onClick={() => {
              void navigate(back);
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={update.isPending}
            onClick={() => {
              update.mutate(
                { id: collection.id, data: handleRef.current?.getJSON() ?? { type: "doc" } },
                {
                  onSuccess: () => {
                    toast.show("Overview saved");
                    void navigate(back);
                  },
                  onError: (error) => {
                    toast.show(error.message, "error");
                  },
                },
              );
            }}
          >
            {update.isPending ? "Saving…" : dirty ? "Save" : "Done"}
          </Button>
        </div>
      </PageHeader>

      <div className="max-w-3xl">
        <h2 className="text-2xl font-semibold">Edit overview</h2>
        <LazyMarkdown
          value={collection.description ?? ""}
          readOnly={false}
          autoFocus
          handleRef={handleRef}
          onChange={() => {
            setDirty(true);
          }}
        />
      </div>
    </>
  );
}
